// Dictionary, 855 part 1 — the 855-specific header body segment: BAK (855's
// beginning segment). Generic X12 855 (5010) definitions only; not tied to
// any trading partner's implementation guide.
//
// Everything else the 855 header body and its Name (N1) loop use — CUR, REF,
// PER, FOB, ITD, DTM, N1-N9 (in dictionary/header-segments.ts) and TD5 (in
// dictionary/856-segments.ts) — is a segment X12 defines once and reuses
// across transaction sets, so it's already defined elsewhere and merged in
// by index.ts. This file does not redefine any of them.

import {LoopId, type ElementDefinition, type SegmentDefinition} from './types';
import {TRANSACTION_SET_PURPOSE_CODES} from './transaction-set-purpose-codes';

function el(
  position: number,
  label: string,
  description: string,
  extra: Partial<ElementDefinition> = {}
): ElementDefinition {
  return {position, label, description, ...extra};
}

export const HEADER_SEGMENTS_855: Record<string, SegmentDefinition> = {
  BAK: {
    id: 'BAK',
    name: 'Beginning Segment for Purchase Order Acknowledgment',
    purpose:
      'Identifies the purchase order being acknowledged and what kind of acknowledgment this is. Required at the start of every 855.',
    loop: LoopId.Header,
    required: true,
    elements: [
      el(
        1,
        'Transaction Set Purpose Code',
        'Whether this is an original acknowledgment, a change, a replacement, etc.',
        {
          dataType: 'code',
          required: true,
          codes: TRANSACTION_SET_PURPOSE_CODES,
        }
      ),
      el(2, 'Acknowledgment Type', 'The overall disposition of the order.', {
        dataType: 'code',
        required: true,
        codes: [
          {code: 'AC', label: 'Acknowledge - With Detail and Change'},
          {code: 'AD', label: 'Acknowledge - With Detail, No Change'},
          {code: 'AE', label: 'Acknowledge - With Exception Detail Only'},
          {code: 'AH', label: 'Acknowledge - Hold Status'},
          {code: 'AK', label: 'Acknowledge - No Detail or Change'},
          {code: 'AP', label: 'Acknowledge - Product Replenishment'},
          {code: 'AT', label: 'Accepted'},
          {code: 'NA', label: 'No Acknowledgment Needed'},
          {code: 'RD', label: 'Reject with Detail'},
          {code: 'RF', label: 'Reject with Exception Detail Only'},
          {code: 'RJ', label: 'Rejected - No Detail'},
          {code: 'RN', label: 'Rejected - Not as Agreed'},
          {code: 'RO', label: 'Rejected With Counter Offer'},
          {code: 'RV', label: 'Rejected - Violates Industry Practices'},
          {code: 'ZZ', label: 'Mutually Defined'},
        ],
      }),
      el(
        3,
        'Purchase Order Number',
        'The purchase order number being acknowledged.',
        {required: true}
      ),
      el(4, 'Date', 'Date of the purchase order being acknowledged.', {
        dataType: 'date',
        format: 'CCYYMMDD',
        required: true,
      }),
      el(
        5,
        'Release Number',
        'A release number against a blanket or master order, when applicable.'
      ),
      el(
        6,
        'Request Reference Number',
        'A reference number tying this acknowledgment back to the request it answers, when applicable.'
      ),
      el(7, 'Contract Number', 'A related contract number, when applicable.'),
      el(8, 'Acknowledgment Date', 'Date this acknowledgment was prepared.', {
        dataType: 'date',
        format: 'CCYYMMDD',
      }),
    ],
  },
};
